import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { User, Save, MapPin, Mail, Phone, Briefcase, Receipt, ShieldCheck, Home, Store, ShoppingBag, Package, ChevronRight, TrendingUp, ExternalLink, Check, X, Loader2 } from 'lucide-react';
import ChangePasswordSection from '../components/ChangePasswordSection';
import SavedAddressesSection from '../components/SavedAddressesSection';
import OrderHistorySection from '../components/OrderHistorySection';
import ChangeEmailSection from '../components/ChangeEmailSection';
import ComplianceWidget from '../components/ComplianceWidget';
import ProfileCapabilities from '../components/ProfileCapabilities';
import ProfilePortfolio from '../components/ProfilePortfolio';
import LocationField from '../components/LocationField';
import { REGION_NAMES, deriveRegionSlug } from '../lib/regionNames';
import ShopTypeChooser from '../components/ShopTypeChooser';
import { ReputationBadge } from '../components/ReputationBadge';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function ProfilePage() {
  const { user, updateProfile, setLanguagePref } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    nickname: '',
    public_name_display: 'full',
    phone: '',
    region: '',
    bio: '',
    address: '',
    postal_code: '',
    location_name: '',
    location_region_slug: '',
    location_district: '',
    location_district_slug: '',
    lat: null,
    lng: null,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [nickStatus, setNickStatus] = useState('idle'); // idle|checking|ok|taken|reserved|format|short
  const [reputation, setReputation] = useState(null);
  const [activeSection, setActiveSection] = useState('account');
  const [myOrders, setMyOrders] = useState([]);
  const [invMine, setInvMine] = useState(null);
  useEffect(() => {
    axios.get(`${API}/orders`).then(({ data }) => {
      const list = Array.isArray(data) ? data : (data?.orders || []);
      setMyOrders(list.slice(0, 8));
    }).catch(() => {});
    axios.get(`${API}/investments/mine`).then(({ data }) => setInvMine(data)).catch(() => {});
  }, []);
  const isInvestor = !!(invMine?.is_investor || user?.is_investor);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const nameParts = (user.name || '').trim().split(/\s+/);
    const derivedFirst = nameParts[0] || '';
    const derivedLast = nameParts.slice(1).join(' ');
    setFormData({
      first_name: user.first_name || derivedFirst,
      last_name: user.first_name ? (user.last_name || '') : derivedLast,
      nickname: user.nickname || '',
      public_name_display: user.public_name_display || 'full',
      phone: user.phone || '',
      region: user.region || deriveRegionSlug(user.location_name),
      bio: user.bio || '',
      address: user.address || '',
      postal_code: user.postal_code || '',
      location_name: user.location_name || '',
      location_region_slug: user.location_region_slug || '',
      location_district: user.location_district || '',
      location_district_slug: user.location_district_slug || '',
      lat: user.lat ?? null,
      lng: user.lng ?? null,
    });
    if (user._id) {
      axios.get(`${API}/reputation/${user._id}`).then(({ data }) => setReputation(data)).catch(() => {});
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live nickname availability check (debounced).
  useEffect(() => {
    const nick = (formData.nickname || '').trim();
    if (!nick) { setNickStatus('idle'); return; }
    if (nick.length < 3) { setNickStatus('short'); return; }
    if (!/^[A-Za-z0-9._-]{3,20}$/.test(nick)) { setNickStatus('format'); return; }
    if (nick.toLowerCase() === (user?.nickname || '').toLowerCase()) { setNickStatus('ok'); return; }
    setNickStatus('checking');
    const handle = setTimeout(() => {
      axios.get(`${API}/auth/nickname-available`, { params: { nickname: nick } })
        .then(({ data }) => {
          if (data.available) setNickStatus('ok');
          else if (data.reason === 'taken') setNickStatus('taken');
          else if (data.reason === 'reserved') setNickStatus('reserved');
          else setNickStatus('format');
        })
        .catch(() => setNickStatus('idle'));
    }, 400);
    return () => clearTimeout(handle);
  }, [formData.nickname, user]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    const result = await updateProfile(formData);
    
    if (result.success) {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (!user) return null;

  const L = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const publicNamePreview = (() => {
    const f = (formData.first_name || '').trim();
    const l = (formData.last_name || '').trim();
    const n = (formData.nickname || '').trim();
    if (formData.public_name_display === 'first') return f || '—';
    if (formData.public_name_display === 'nickname') return n ? ('@' + n) : (f || '—');
    return (f + ' ' + l).trim() || '—';
  })();

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="profile-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-[#D97736] flex items-center justify-center text-white text-3xl font-bold">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-[#121212]">
                {user.name}
              </h1>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#5C5C5C]">
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  <span>{user.email}</span>
                </div>
                {user.region && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{REGION_NAMES[user.region] || user.region}</span>
                  </div>
                )}
              </div>
              {user.is_seller && (
                <span className="inline-block mt-2 px-3 py-1 bg-[#4A6B53] text-white text-xs rounded-full">
                  {t('sellerProfile')}
                </span>
              )}
              {reputation && (
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <ReputationBadge level={reputation.level} levelInfo={reputation.level_info}
                    avgRating={reputation.avg_rating} reviewCount={reputation.review_count} />
                </div>
              )}
              <Link
                to={`/lietotajs/${user._id}`}
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-[#2A2320] transition-colors"
                data-testid="view-public-profile-link"
              >
                <ExternalLink className="w-4 h-4" />
                {language === 'lv' ? 'Atvērt manu publisko lapu' : language === 'ru' ? 'Открыть мою публичную страницу' : 'Open my public page'}
              </Link>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Section navigation */}
          <aside className="lg:col-span-3 lg:sticky lg:top-8">
            <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="profile-nav">
              {[
                { id: 'account', icon: User, lv: 'Konts', en: 'Account', ru: 'Аккаунт' },
                { id: 'addresses', icon: Home, lv: 'Adreses', en: 'Addresses', ru: 'Адреса' },
                { id: 'security', icon: ShieldCheck, lv: 'Drošība', en: 'Security', ru: 'Безопасность' },
                { id: 'orders', icon: ShoppingBag, lv: 'Pirkumu vēsture', en: 'Purchase history', ru: 'История покупок' },
                ...(isInvestor ? [{ id: 'investor', icon: TrendingUp, lv: 'Investors', en: 'Investor', ru: 'Инвестор' }] : []),
              ].map((s) => {
                const Icon = s.icon;
                const isActive = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveSection(s.id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap lg:whitespace-normal text-left ${isActive ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'text-[#5C5C5C] hover:bg-[#E5E4E0]/40 hover:text-[#121212]'}`}
                    data-testid={`profile-nav-${s.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {s[language] || s.lv}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Section content */}
          <div className="lg:col-span-9 space-y-6 min-w-0">
            {activeSection === 'account' && (
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8">
              <h2 className="font-outfit text-xl font-semibold text-[#121212] mb-6">
                {t('updateProfile')}
              </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="error-message">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm" data-testid="success-message">
                {language === 'lv' ? 'Profils atjaunināts!' : language === 'ru' ? 'Профиль обновлён!' : 'Profile updated!'}
              </div>
            )}

            {/* ── Pamatinformācija ───────────────────────────────── */}
            <div className="space-y-5">
              <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">
                {language === 'lv' ? 'Pamatinformācija' : language === 'ru' ? 'Основная информация' : 'Basic information'}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-[#121212] font-medium">
                  {L('Vārds', 'First name', 'Имя')}
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  data-testid="first-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-[#121212] font-medium">
                  {L('Uzvārds (nav obligāts)', 'Last name (optional)', 'Фамилия (необязательно)')}
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  data-testid="last-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nickname" className="text-[#121212] font-medium">
                  {L('Iesauka', 'Nickname', 'Псевдоним')}
                </Label>
                <Input
                  id="nickname"
                  name="nickname"
                  value={formData.nickname}
                  onChange={(e) => setFormData((p) => ({ ...p, nickname: e.target.value.replace(/[^A-Za-z0-9._-]/g, '').slice(0, 20) }))}
                  placeholder={L('piem. janis_b', 'e.g. janis_b', 'напр. janis_b')}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  data-testid="nickname-input"
                />
                {nickStatus !== 'idle' && (formData.nickname || '').trim() && (
                  <p
                    className={`text-xs flex items-center gap-1.5 ${nickStatus === 'ok' ? 'text-[#15803D]' : nickStatus === 'checking' ? 'text-[#9CA3AF]' : 'text-[#DC2626]'}`}
                    data-testid="nickname-status"
                  >
                    {nickStatus === 'checking' && <><Loader2 className="w-3.5 h-3.5 animate-spin" />{L('Pārbauda…', 'Checking…', 'Проверка…')}</>}
                    {nickStatus === 'ok' && <><Check className="w-3.5 h-3.5" />{L('@' + formData.nickname.trim() + ' ir brīva', '@' + formData.nickname.trim() + ' is available', '@' + formData.nickname.trim() + ' свободен')}</>}
                    {nickStatus === 'taken' && <><X className="w-3.5 h-3.5" />{L('Šī iesauka jau ir aizņemta', 'This nickname is taken', 'Этот псевдоним занят')}</>}
                    {nickStatus === 'reserved' && <><X className="w-3.5 h-3.5" />{L('Šī iesauka ir rezervēta un nav pieejama', 'This nickname is reserved and unavailable', 'Этот псевдоним зарезервирован и недоступен')}</>}
                    {nickStatus === 'short' && <><X className="w-3.5 h-3.5" />{L('Vismaz 3 rakstzīmes', 'At least 3 characters', 'Минимум 3 символа')}</>}
                    {nickStatus === 'format' && <><X className="w-3.5 h-3.5" />{L('Tikai burti, cipari, . _ -', 'Only letters, digits, . _ -', 'Только буквы, цифры, . _ -')}</>}
                  </p>
                )}
                <p className="text-xs text-[#9CA3AF]">
                  {L('Unikāla visā KoLV · 3–20 rakstzīmes (burti, cipari, . _ -)', 'Unique across KoLV · 3–20 chars (letters, digits, . _ -)', 'Уникален во всём KoLV · 3–20 символов')}
                </p>
              </div>

              {/* How the name is shown publicly (public profile + referral QR) */}
              <div className="space-y-2 pt-1">
                <Label className="text-[#121212] font-medium">
                  {L('Kā rādīt publiskajā profilā un QR', 'How to show on public profile & QR', 'Как показывать в публичном профиле и QR')}
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="public-name-display">
                  {[
                    { k: 'first', lv: 'Vārds', en: 'First name', ru: 'Имя' },
                    { k: 'full', lv: 'Vārds + uzvārds', en: 'First + last', ru: 'Имя + фамилия' },
                    { k: 'nickname', lv: 'Iesauka', en: 'Nickname', ru: 'Псевдоним' },
                  ].map((o) => (
                    <button
                      key={o.k}
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, public_name_display: o.k }))}
                      className={`h-11 px-3 rounded-xl border text-sm font-medium transition-colors ${formData.public_name_display === o.k ? 'border-[#D97736] bg-[#FFF8F0] text-[#D97736]' : 'border-[#E5E4E0] bg-white text-[#5C5C5C] hover:border-[#C9C8C4]'}`}
                      data-testid={`public-name-${o.k}`}
                    >
                      {o[language] || o.lv}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-[#5C5C5C]">
                  {L('Publiski redzams:', 'Shown publicly:', 'Публично видно:')}{' '}
                  <span className="font-semibold text-[#121212]" data-testid="public-name-preview">{publicNamePreview}</span>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#121212] font-medium">
                  {t('phone')}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  placeholder="+371 20000000"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* ── Mana atrašanās vieta (no onboarding) ───────────── */}
            <div className="space-y-5 pt-6 border-t border-[#E5E4E0]">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">
                  {language === 'lv' ? 'Mana atrašanās vieta' : language === 'ru' ? 'Мое местоположение' : 'My location'}
                </h3>
                <p className="text-xs text-[#5C5C5C] mt-1">
                  {language === 'lv' ? 'Šo izvēlējies reģistrācijā. Vari mainīt jebkurā laikā.'
                    : language === 'ru' ? 'Вы выбрали это при регистрации. Можно изменить в любое время.'
                    : 'You chose this during onboarding. You can change it anytime.'}
                </p>
              </div>

              <LocationField
                value={{
                  region: formData.region,
                  location_name: formData.location_name,
                  location_region_slug: formData.location_region_slug,
                  location_district: formData.location_district,
                  location_district_slug: formData.location_district_slug,
                  lat: formData.lat,
                  lng: formData.lng,
                }}
                onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
                testid="profile-location-field"
              />
            </div>

            {/* ── Valoda ──────────────────────────────────────────── */}
            <div className="space-y-4 pt-6 border-t border-[#E5E4E0]" data-testid="profile-language-section">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">
                  {language === 'lv' ? 'Valoda' : language === 'ru' ? 'Язык' : 'Language'}
                </h3>
                <p className="text-xs text-[#5C5C5C] mt-1">
                  {language === 'lv' ? 'Izvēlies platformas valodu.'
                    : language === 'ru' ? 'Выберите язык платформы.'
                    : 'Choose the platform language.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { code: 'lv', flag: '🇱🇻', label: 'Latviešu' },
                  { code: 'en', flag: '🇬🇧', label: 'English' },
                  { code: 'ru', flag: '🇷🇺', label: 'Русский' },
                ].map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => { setLanguage(l.code); if (setLanguagePref) setLanguagePref(l.code); }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                      language === l.code
                        ? 'bg-[#D97736] text-white border-[#D97736]'
                        : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F3F2EE]'
                    }`}
                    data-testid={`profile-lang-${l.code}`}
                  >
                    <span>{l.flag}</span> {l.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Restart onboarding */}
            <div className="rounded-2xl border border-[#E5E4E0] p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">
                  {language === 'lv' ? 'Reģions un apkaime' : language === 'ru' ? 'Регион и район' : 'Region & area'}
                </h3>
                <p className="text-xs text-[#5C5C5C] mt-1">
                  {language === 'lv' ? 'Iziet ievadi no jauna, lai mainītu savu reģionu un apkaimi kartē.'
                    : language === 'ru' ? 'Пройдите онбординг заново, чтобы изменить регион и район на карте.'
                    : 'Re-run onboarding to change your region and area on the map.'}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/welcome?restart=1')}
                className="border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white whitespace-nowrap"
                data-testid="restart-onboarding-btn"
              >
                <MapPin className="w-4 h-4 mr-1.5" />
                {language === 'lv' ? 'Atkārtot ievadi' : language === 'ru' ? 'Пройти заново' : 'Restart onboarding'}
              </Button>
            </div>


            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#D97736] hover:bg-[#C06528] text-white font-medium"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('loading')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {t('save')}
                </span>
              )}
            </Button>
          </form>
            </div>
            )}

            {activeSection === 'addresses' && <SavedAddressesSection />}

            {/* ── Account security ───────────────────────────────── */}
            {activeSection === 'security' && (
              <>
                <ChangeEmailSection
                  currentEmail={user?.email}
                  onChanged={() => window.location.reload()}
                />
                <ChangePasswordSection />
              </>
            )}

            {/* ── Purchase history ─────────────────────────────────── */}
            {activeSection === 'orders' && (
              <>
                <OrderHistorySection />
                <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8" data-testid="purchase-history-section">
                <div className="flex items-center gap-2 mb-1">
                  <ShoppingBag className="w-5 h-5 text-[#D97736]" />
                  <h2 className="font-outfit text-xl font-semibold text-[#121212]">
                    {language === 'lv' ? 'Pirkumu vēsture' : language === 'ru' ? 'История покупок' : 'Purchase history'}
                  </h2>
                </div>
                <p className="text-sm text-[#5C5C5C] mb-5">
                  {language === 'lv' ? 'Tavi pasūtījumi un to statuss.' : language === 'ru' ? 'Ваши заказы и их статус.' : 'Your orders and their status.'}
                </p>
                {myOrders.length === 0 ? (
                  <p className="text-sm text-[#9CA3AF]" data-testid="no-orders">
                    {language === 'lv' ? 'Vēl nav pasūtījumu.' : language === 'ru' ? 'Пока нет заказов.' : 'No orders yet.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {myOrders.map((o) => (
                      <button key={o.id} onClick={() => navigate('/orders')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#F3F2EE] hover:border-[#D97736] transition-colors text-left" data-testid={`order-row-${o.id}`}>
                        <div className="w-9 h-9 rounded-lg bg-[#FFF6F0] flex items-center justify-center flex-shrink-0"><Package className="w-4 h-4 text-[#D97736]" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#121212]">#{o.id.slice(-6).toUpperCase()} · €{(o.total || 0).toFixed(2)}</p>
                          <p className="text-xs text-[#5C5C5C]">{(o.created_at || '').slice(0, 10)} · {o.status || o.payment_status}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                      </button>
                    ))}
                    <button onClick={() => navigate('/orders')} className="w-full text-center text-sm text-[#D97736] font-medium py-2 hover:underline" data-testid="view-all-orders">
                      {language === 'lv' ? 'Skatīt visus pasūtījumus →' : language === 'ru' ? 'Все заказы →' : 'View all orders →'}
                    </button>
                  </div>
                )}
              </div>
              </>
            )}

            {activeSection === 'investor' && invMine && (
              <div className="space-y-6" data-testid="investor-section">
                <div className="rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-[#1A1A1A] to-[#2A2320] text-white border border-[#3A3230]">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-5 h-5 text-[#D97736]" />
                    <h2 className="font-outfit text-xl font-semibold">
                      {language === 'lv' ? 'Mans investora panelis' : language === 'ru' ? 'Панель инвестора' : 'My investor panel'}
                    </h2>
                  </div>
                  <p className="text-sm text-white/60 mb-6">
                    {language === 'lv'
                      ? `${invMine.profit_share_pct}% no KoLV peļņas tiek sadalīti proporcionāli starp visiem investoriem.`
                      : language === 'ru'
                      ? `${invMine.profit_share_pct}% прибыли KoLV распределяются пропорционально между всеми инвесторами.`
                      : `${invMine.profit_share_pct}% of KoLV profit is shared proportionally among all investors.`}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="investor-total">
                      <p className="text-xs uppercase tracking-wide text-white/50 mb-1">
                        {language === 'lv' ? 'Kopā ieguldīts' : language === 'ru' ? 'Всего вложено' : 'Total invested'}
                      </p>
                      <p className="font-outfit text-3xl font-bold text-white">€{(invMine.total_invested || 0).toFixed(2)}</p>
                    </div>
                    <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="investor-share">
                      <p className="text-xs uppercase tracking-wide text-white/50 mb-1">
                        {language === 'lv' ? 'Mana daļa no fonda' : language === 'ru' ? 'Моя доля в фонде' : 'My share of the pool'}
                      </p>
                      <p className="font-outfit text-3xl font-bold text-[#D97736]">{(invMine.share_pct_of_pool || 0).toFixed(2)}%</p>
                      <p className="text-xs text-white/40 mt-1">
                        {language === 'lv'
                          ? `no €${(invMine.pool_total || 0).toFixed(2)} kopējā investoru fonda`
                          : language === 'ru'
                          ? `из €${(invMine.pool_total || 0).toFixed(2)} общего фонда`
                          : `of €${(invMine.pool_total || 0).toFixed(2)} total investor pool`}
                      </p>
                    </div>
                  </div>

                  {(invMine.private_total > 0 && invMine.company_total > 0) && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="investor-private-total">
                        <p className="text-xs text-white/50 mb-1">{language === 'lv' ? 'Kā privātpersona' : language === 'ru' ? 'Как физлицо' : 'As private person'}</p>
                        <p className="font-outfit text-xl font-bold">€{invMine.private_total.toFixed(2)}</p>
                      </div>
                      <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="investor-company-total">
                        <p className="text-xs text-white/50 mb-1">{language === 'lv' ? 'Kā uzņēmums' : language === 'ru' ? 'Как компания' : 'As company'}</p>
                        <p className="font-outfit text-xl font-bold">€{invMine.company_total.toFixed(2)}</p>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-white/40 mt-6 leading-relaxed">
                    {language === 'lv'
                      ? 'Aprēķinātā peļņa tiks parādīta pēc KoLV gada peļņas noteikšanas — tava daļa no peļņas fonda ir norādīta procentos augstāk.'
                      : language === 'ru'
                      ? 'Рассчитанная прибыль будет показана после определения годовой прибыли KoLV — ваша доля в фонде указана выше в процентах.'
                      : 'Calculated profit will be shown once KoLV annual profit is determined — your share of the profit pool is shown above as a percentage.'}
                  </p>
                </div>

                {invMine.investments?.length > 0 && (
                  <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8">
                    <h3 className="font-outfit text-lg font-semibold text-[#121212] mb-4">
                      {language === 'lv' ? 'Mani ieguldījumi' : language === 'ru' ? 'Мои вложения' : 'My investments'}
                    </h3>
                    <div className="space-y-2">
                      {invMine.investments.map((iv, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-[#F3F2EE]" data-testid={`investment-row-${i}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#121212] truncate">{iv.label}</p>
                            <p className="text-xs text-[#5C5C5C]">
                              {(iv.paid_at || '').slice(0, 10)} · {iv.investor_type === 'company' ? (language === 'lv' ? 'Uzņēmums' : language === 'ru' ? 'Компания' : 'Company') : (language === 'lv' ? 'Privātpersona' : language === 'ru' ? 'Физлицо' : 'Private')}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-[#D97736]">€{(iv.amount || 0).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
